// packages/front/src/portfolio/portfolio.hooks.ts
//
// Stage 3 (part 1) — Portfolio server-state layer on TanStack Query.
//
// All reads go through useApiQuery (wrapper over useQuery + apiFetch).
// Mutations with dynamic URLs (/:id) use useMutation + apiFetch + queryClient
// .invalidateQueries in onSuccess. Fixed-URL POST mutations use useApiMutation.
// Query keys come from the single registry `qk` — no inline key literals.
//
// Invalidation matrix (onSuccess of every mutation):
//   transaction CRUD  → positions, summary, transactions, cashflows (paired CF auto-created)
//   cashflow CRUD     → positions (cost basis), summary, cashflows
//   account CRUD      → accounts, positions (accountName projection), summary (allocationByAccount)
//   exchangeRate C/refresh → positions (USD conversion), summary, exchangeRates
//   bulkImport        → positions, summary, transactions, cashflows, accounts (auto-create), symbols (auto-create)

import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { CORPORATE_ACTION_TYPE_OBJ, PORTFOLIO_INSTRUMENT_KIND_OBJ, isPortfolioComparisonSyncStatus, isPortfolioPriceSyncActive,
  type PortfolioInstrumentRef, type PortfolioComparisonSyncStatus } from "@signals/shared";
import { apiFetch, qk, useApiMutation, useApiQuery } from "../_common";
import type {
  Account,
  AccountFilters,
  CashFlow,
  CashFlowFilters,
  CloseAccountInput,
  CreateAccountInput,
  CreateCashFlowInput,
  CreateExchangeRateInput,
  CreateIncomeEventInput,
  CreateTransactionInput,
  ExchangeRate,
  IncomeEvent,
  IncomeEventFilters,
  ItemsResponse,
  MissingValuation,
  PortfolioCompareResponse,
  Portfolio,
  PortfolioSummary,
  Position,
  PositionHistoryConversionEvent,
  PositionHistoryEvent,
  PositionHistoryFilters,
  PositionHistoryTransactionEvent,
  PositionHistoryOptionEvent,
  SymbolRecord,
  TabRow,
  Transaction,
  TransactionFilters,
  ValuationStatus,
  YearlyPerformance,
} from "./portfolio.types";

// Re-export types so the page-component imports them from hooks (single import site).
export type {
  Account,
  AccountFilters,
  CashFlow,
  CashFlowFilters,
  CloseAccountInput,
  CreateAccountInput,
  CreateCashFlowInput,
  CreateExchangeRateInput,
  CreateIncomeEventInput,
  CreateTransactionInput,
  ExchangeRate,
  IncomeEvent,
  IncomeEventFilters,
  ItemsResponse,
  MissingValuation,
  PortfolioCompareResponse,
  Portfolio,
  PortfolioSummary,
  Position,
  PositionHistoryConversionEvent,
  PositionHistoryEvent,
  PositionHistoryFilters,
  PositionHistoryTransactionEvent,
  PositionHistoryOptionEvent,
  SymbolRecord,
  TabRow,
  Transaction,
  TransactionFilters,
  ValuationStatus,
  YearlyPerformance,
};
export type { PortfolioComparisonPreparation, PortfolioComparisonSyncStatus, PortfolioComparisonNeed, PortfolioComparisonTaskReason, PortfolioComparisonBoundary } from "./portfolio.types";

// Keep the legacy `Symbol` interface name exported for backward compatibility
// with any consumer expecting the old shape (useSymbols returned { items: Symbol[] }).
export interface Symbol {
  id: string;
  symbol: string;
  name: string | null;
  exchange: string | null;
  type: string | null;
  country: string | null;
  currency: string | null;
}

// ---------------------------------------------------------------------------
// URL builders
// ---------------------------------------------------------------------------

function buildUrl(base: string, params: object): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") sp.set(k, String(v));
  }
  const qs = sp.toString();
  return qs ? `${base}?${qs}` : base;
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** Read: list all portfolios. */
export function usePortfolios() {
  return useApiQuery<ItemsResponse<Portfolio>>(
    qk.portfolio.portfolios,
    "/api/portfolio/portfolios",
  );
}

/** Summary recalculates every valuation component and can take over 30 s (the default request timeout). */
const PORTFOLIO_SUMMARY_TIMEOUT_MS = 120_000;
const PORTFOLIO_PRICE_STATUS_POLL_MS = 2000;

/** Read: portfolio summary (optionally as-of a date). */
export function usePortfolioSummary(portfolioId: string = "", date?: string) {
  return useApiQuery<PortfolioSummary>(
    qk.portfolio.summary(portfolioId, date),
    buildUrl("/api/portfolio/summary", { portfolioId, date }),
    { enabled: !!portfolioId, timeoutMs: PORTFOLIO_SUMMARY_TIMEOUT_MS },
  );
}

export interface PortfolioSnapshotRevision {
  id: string;
  revision: number;
  policyVersion: number;
  reason: string;
  evidenceIds: string[];
  snapshotData: Record<string, unknown>;
}

export function usePortfolioSnapshot(portfolioId: string, date: string, enabled = true) {
  return useApiQuery<Record<string, unknown> & { currentRevisionId: string | null }>(
    qk.portfolio.snapshot(portfolioId, date),
    `/api/portfolio/snapshots/${encodeURIComponent(date)}?portfolioId=${encodeURIComponent(portfolioId)}`,
    { enabled: !!portfolioId && !!date && enabled },
  );
}

export function usePortfolioSnapshotRevisions(portfolioId: string, date: string, enabled = true) {
  return useApiQuery<{ items: PortfolioSnapshotRevision[] }>(
    qk.portfolio.snapshotRevisions(portfolioId, date),
    `/api/portfolio/snapshots/${encodeURIComponent(date)}/revisions?portfolioId=${encodeURIComponent(portfolioId)}`,
    { enabled: !!portfolioId && !!date && enabled },
  );
}

export interface PortfolioSnapshotRecalculationPreview {
  portfolioId: string;
  date: string;
  expectedCurrentRevisionId: string | null;
  before: Record<string, unknown>;
  after: Record<string, unknown>;
  delta: {
    totalValueUSD: number | null;
    partialValueUSD: number | null;
    totalUnrealizedPnLUSD: number | null;
    partialUnrealizedPnLUSD: number | null;
  };
}

function snapshotMutationQueryKeys(portfolioId: string, date: string) {
  return [
    qk.portfolio.snapshot(portfolioId, date),
    qk.portfolio.snapshotRevisions(portfolioId, date),
    qk.portfolio.snapshots(portfolioId),
    qk.portfolio.summaryForPortfolio(portfolioId),
    qk.portfolio.positionsForPortfolio(portfolioId),
    qk.portfolio.compareForPortfolio(portfolioId),
    qk.portfolio.yearlyPerformance.all(portfolioId),
    qk.portfolio.yearlyPerformance.singleForPortfolio(portfolioId),
  ];
}

/** Preview is read-only: it never invalidates or replaces saved valuations. */
export function usePreviewPortfolioSnapshotRecalculation(portfolioId: string, date: string) {
  return useApiMutation<void, PortfolioSnapshotRecalculationPreview>(
    `/api/portfolio/snapshots/${encodeURIComponent(date)}/recalculation/preview`,
    "POST",
    { body: () => ({ portfolioId }) },
  );
}

export function useApplyPortfolioSnapshotRecalculation(portfolioId: string, date: string) {
  return useApiMutation<{ expectedCurrentRevisionId: string | null }>(
    `/api/portfolio/snapshots/${encodeURIComponent(date)}/recalculation/apply`,
    "POST",
    {
      body: (input) => ({ portfolioId, ...input }),
      invalidateKeys: snapshotMutationQueryKeys(portfolioId, date),
    },
  );
}

export function useRestorePortfolioSnapshotRevision(portfolioId: string, date: string) {
  const queryClient = useQueryClient();
  return useMutation<
    { restored: boolean; date: string; revisionId: string },
    Error,
    { revisionId: string; expectedCurrentRevisionId: string | null }
  >({
    mutationFn: ({ revisionId, expectedCurrentRevisionId }) =>
      apiFetch(`/api/portfolio/snapshots/${encodeURIComponent(date)}/revisions/restore`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ portfolioId, revisionId, expectedCurrentRevisionId }),
      }),
    onSuccess: () => {
      for (const queryKey of snapshotMutationQueryKeys(portfolioId, date)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}

/** Read: portfolio positions (optionally as-of a date, filtered by account). */
export function usePortfolioPositions(
  portfolioId: string = "",
  date?: string,
  accountId?: string,
  status?: "open" | "closed" | "all",
  enabled: boolean = true,
) {
  return useApiQuery<ItemsResponse<Position>>(
    qk.portfolio.positions(portfolioId, date, accountId, status),
    buildUrl("/api/portfolio/positions", {
      portfolioId,
      date,
      accountId,
      status,
    }),
    { enabled: !!portfolioId && enabled },
  );
}

/** Read: transactions list (filtered by account / date range). */
export function usePortfolioTransactions(
  portfolioId: string = "",
  filters?: TransactionFilters,
) {
  return useApiQuery<ItemsResponse<TabRow>>(
    qk.portfolio.transactions(portfolioId, filters),
    buildUrl("/api/portfolio/transactions", {
      portfolioId,
      ...(filters ?? {}),
    }),
    { enabled: !!portfolioId },
  );
}

/** Read: trades and saved conversions along the selected position's history. */
export function usePortfolioPositionHistory(
  portfolioId: string = "",
  instrument: string | PortfolioInstrumentRef = "",
  valuationDate: string = "",
  filters?: PositionHistoryFilters,
  enabled: boolean = true,
) {
  const ref = typeof instrument === "string" ? { symbolId: instrument }
    : instrument.kind === PORTFOLIO_INSTRUMENT_KIND_OBJ.SECURITY ? { symbolId: instrument.symbolId } : { optionContractId: instrument.optionContractId };
  const instrumentId = "symbolId" in ref ? ref.symbolId : ref.optionContractId;
  return useApiQuery<ItemsResponse<PositionHistoryEvent>>(
    qk.portfolio.positionHistory(
      portfolioId,
      instrument,
      valuationDate,
      filters,
    ),
    buildUrl("/api/portfolio/position-history", {
      portfolioId,
      ...ref,
      valuationDate,
      ...(filters ?? {}),
    }),
    { enabled: !!portfolioId && !!instrumentId && !!valuationDate && enabled },
  );
}

/** Read: cashflows list (filtered by account / date range). */
export function usePortfolioCashflows(
  portfolioId: string = "",
  filters?: CashFlowFilters,
) {
  return useApiQuery<ItemsResponse<TabRow>>(
    qk.portfolio.cashflows(portfolioId, filters),
    buildUrl("/api/portfolio/cashflows", { portfolioId, ...(filters ?? {}) }),
    { enabled: !!portfolioId },
  );
}

/** Read: income events list (filtered by account / date range). */
export function useIncomeEvents(
  portfolioId: string = "",
  filters?: IncomeEventFilters,
) {
  return useApiQuery<ItemsResponse<IncomeEvent>>(
    qk.portfolio.incomeEvents.list(portfolioId, filters),
    buildUrl("/api/portfolio/income-events", {
      portfolioId,
      ...(filters ?? {}),
    }),
    { enabled: !!portfolioId },
  );
}

/** Read: accounts list for a portfolio. */
export function usePortfolioAccounts(
  portfolioId: string = "",
  filters: AccountFilters = {},
) {
  return useApiQuery<ItemsResponse<Account>>(
    qk.portfolio.accounts(portfolioId, filters),
    buildUrl("/api/portfolio/accounts", { portfolioId, ...filters }),
    { enabled: !!portfolioId },
  );
}

/** Read: exchange-rates list. */
export function usePortfolioExchangeRates() {
  return useApiQuery<ItemsResponse<TabRow>>(
    qk.portfolio.exchangeRates,
    "/api/portfolio/exchange-rates",
  );
}

/** Read: symbols list (legacy limit-based fetch for comboboxes). */
export function useSymbols() {
  return useApiQuery<{ items: Symbol[] }>(
    qk.symbols.list({ limit: 500 }),
    "/api/symbols?limit=500",
  );
}

// ---------------------------------------------------------------------------
// Mutations — Transactions
// ---------------------------------------------------------------------------

function transactionMutationQueryKeys(portfolioId: string, includeFinancials: boolean) {
  return [
    qk.portfolio.transactionsForPortfolio(portfolioId),
    qk.portfolio.positionHistoryForPortfolio(portfolioId),
    qk.portfolio.positionsForPortfolio(portfolioId),
    qk.portfolio.summaryForPortfolio(portfolioId),
    ...(includeFinancials
      ? [
          qk.portfolio.cashflowsForPortfolio(portfolioId),
          qk.portfolio.compareForPortfolio(portfolioId),
          qk.portfolio.yearlyPerformance.all(portfolioId),
          qk.portfolio.yearlyPerformance.singleForPortfolio(portfolioId),
          qk.portfolio.snapshots(portfolioId),
        ]
      : []),
  ] as const;
}

async function cancelTransactionMutationQueries(
  queryClient: QueryClient,
  portfolioId: string,
  includeFinancials: boolean,
) {
  await Promise.all(
    transactionMutationQueryKeys(portfolioId, includeFinancials).map(
      (queryKey) => queryClient.cancelQueries({ queryKey }),
    ),
  );
}

function invalidateTransactionMutationQueries(
  queryClient: QueryClient,
  portfolioId: string,
  includeFinancials: boolean,
) {
  for (const queryKey of transactionMutationQueryKeys(portfolioId, includeFinancials)) {
    // Do not make a transaction save wait for the heavier Summary calculation.
    void queryClient.invalidateQueries({ queryKey });
  }
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function rowMatchesDateAndAccount(
  row: Record<string, unknown>,
  filters: Record<string, unknown>,
): boolean {
  if (typeof filters["accountId"] === "string" && row["accountId"] !== filters["accountId"]) {
    return false;
  }
  const date = typeof row["date"] === "string" ? row["date"] : undefined;
  if (typeof filters["from"] === "string" && (!date || date < filters["from"])) return false;
  if (typeof filters["to"] === "string" && (!date || date > filters["to"])) return false;
  return true;
}

function updateRowsInPortfolioCaches(
  queryClient: QueryClient,
  portfolioId: string,
  transaction: Transaction,
) {
  if (!transaction.id) return;

  const updateMatchingRows = (
    queryKey: readonly unknown[],
    matches: (row: Record<string, unknown>, key: readonly unknown[]) => boolean,
  ) => {
    for (const [key, cached] of queryClient.getQueriesData<ItemsResponse<TabRow>>({ queryKey })) {
      if (!cached || !Array.isArray(cached.items)) continue;
      let changed = false;
      const items: TabRow[] = [];
      for (const item of cached.items) {
        if (!isObjectRecord(item) || item["id"] !== transaction.id || item["eventType"] === CORPORATE_ACTION_TYPE_OBJ.CONVERSION) {
          items.push(item);
          continue;
        }
        const merged: TabRow = { ...item, ...transaction };
        changed = true;
        if (matches(merged, key)) items.push(merged);
      }
      if (changed) queryClient.setQueryData<ItemsResponse<TabRow>>(key, { ...cached, items });
    }
  };

  updateMatchingRows(
    qk.portfolio.transactionsForPortfolio(portfolioId),
    (row, key) => rowMatchesDateAndAccount(
      row,
      isObjectRecord(key[3]) ? key[3] : {},
    ),
  );
  updateMatchingRows(
    qk.portfolio.positionHistoryForPortfolio(portfolioId),
    (row, key) => {
      const params = isObjectRecord(key[3]) ? key[3] : {};
      const date = typeof row["date"] === "string" ? row["date"] : undefined;
      // The server's conversion chain can include purchases of predecessor
      // symbols. Keep those existing rows; refetch resolves structural edits.
      if (typeof params["valuationDate"] === "string" && (!date || date > params["valuationDate"])) return false;
      return rowMatchesDateAndAccount(
        row,
        isObjectRecord(params["filters"]) ? params["filters"] : {},
      );
    },
  );
}

/** Create a transaction. Keeps transaction-related refreshes scoped to its portfolio. */
export function useCreateTransaction(portfolioId: string) {
  const queryClient = useQueryClient();
  return useMutation<Transaction, Error, CreateTransactionInput>({
    mutationFn: (body) =>
      apiFetch<Transaction>("/api/portfolio/transactions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onMutate: () => cancelTransactionMutationQueries(queryClient, portfolioId, true),
    onSuccess: async () => {
      await cancelTransactionMutationQueries(queryClient, portfolioId, true);
      invalidateTransactionMutationQueries(queryClient, portfolioId, true);
    },
  });
}

/** Update a transaction and merge its confirmed fields into existing rows. */
export function useUpdateTransaction(portfolioId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    Transaction,
    Error,
    { id: string; body: Partial<Transaction> }
  >({
    mutationFn: ({ id, body }) =>
      apiFetch<Transaction>(`/api/portfolio/transactions/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onMutate: () => cancelTransactionMutationQueries(queryClient, portfolioId, true),
    onSuccess: async (updated) => {
      await cancelTransactionMutationQueries(queryClient, portfolioId, true);
      updateRowsInPortfolioCaches(queryClient, portfolioId, updated);
      invalidateTransactionMutationQueries(queryClient, portfolioId, true);
    },
  });
}

/** Set or clear the blocked quantity and update the portfolio-scoped views. */
export function useSetTransactionBlockedQuantity() {
  const queryClient = useQueryClient();
  return useMutation<
    Transaction,
    Error,
    { id: string; portfolioId: string; blockedQuantity: number }
  >({
    mutationFn: ({ id, portfolioId, blockedQuantity }) =>
      apiFetch<Transaction>(`/api/portfolio/transactions/${id}/blocked`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ portfolioId, blockedQuantity }),
      }),
    onMutate: ({ portfolioId }) =>
      cancelTransactionMutationQueries(queryClient, portfolioId, false),
    onSuccess: async (updated, { portfolioId }) => {
      await cancelTransactionMutationQueries(queryClient, portfolioId, false);
      updateRowsInPortfolioCaches(queryClient, portfolioId, updated);
      invalidateTransactionMutationQueries(queryClient, portfolioId, false);
    },
  });
}

/** Delete a transaction by id. Invalidates positions, summary, transactions, cashflows. */
export function useDeleteTransaction() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) =>
      apiFetch<void>(`/api/portfolio/transactions/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.portfolio.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Mutations — CashFlows
// ---------------------------------------------------------------------------

/** Create a cashflow. Invalidates positions (cost basis), summary, cashflows. */
export function useCreateCashFlow() {
  return useApiMutation<CreateCashFlowInput, CashFlow>(
    "/api/portfolio/cashflows",
    "POST",
    {
      invalidateKeys: [qk.portfolio.all],
    },
  );
}

/** Delete a cashflow by id. Invalidates positions, summary, cashflows. */
export function useDeleteCashFlow() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) =>
      apiFetch<void>(`/api/portfolio/cashflows/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.portfolio.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Mutations — Income Events
// ---------------------------------------------------------------------------

/** Create an income event. Invalidates positions, summary, income events, cashflows. */
export function useCreateIncomeEvent() {
  const queryClient = useQueryClient();
  return useMutation<
    IncomeEvent,
    Error,
    { portfolioId: string; body: CreateIncomeEventInput }
  >({
    mutationFn: ({ portfolioId, body }) =>
      apiFetch<IncomeEvent>(
        `/api/portfolio/income-events?portfolioId=${encodeURIComponent(portfolioId)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.portfolio.all });
    },
  });
}

/** Update an income event by id. Invalidates positions, summary, income events, cashflows. */
export function useUpdateIncomeEvent() {
  const queryClient = useQueryClient();
  return useMutation<
    IncomeEvent,
    Error,
    { id: string; portfolioId: string; body: Partial<IncomeEvent> }
  >({
    mutationFn: ({ id, portfolioId, body }) =>
      apiFetch<IncomeEvent>(
        `/api/portfolio/income-events/${id}?portfolioId=${encodeURIComponent(portfolioId)}`,
        {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.portfolio.all });
    },
  });
}

/** Delete an income event by id. Invalidates positions, summary, income events, cashflows. */
export function useDeleteIncomeEvent() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) =>
      apiFetch<void>(`/api/portfolio/income-events/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.portfolio.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Mutations — Accounts
// ---------------------------------------------------------------------------

/** Create an account. Invalidates accounts, positions (accountName projection), summary. */
export function useCreateAccount() {
  return useApiMutation<CreateAccountInput, Account>(
    "/api/portfolio/accounts",
    "POST",
    {
      invalidateKeys: [
        qk.portfolio.all, // covers accounts + positions + summary variants
      ],
    },
  );
}

/** Update an account by id. Invalidates accounts, positions, summary. */
export function useUpdateAccount() {
  const queryClient = useQueryClient();
  return useMutation<Account, Error, { id: string; body: Partial<Account> }>({
    mutationFn: ({ id, body }) =>
      apiFetch<Account>(`/api/portfolio/accounts/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.portfolio.all });
    },
  });
}

/**
 * Close an empty account on a date. The server refuses (409) while money, securities or later
 * records remain. Invalidates this portfolio's accounts for every date and filter, and its
 * summaries (cleaned allocations); other portfolios' caches are untouched.
 */
export function useClosePortfolioAccount(portfolioId: string) {
  const queryClient = useQueryClient();
  return useMutation<Account, Error, { id: string; body: Omit<CloseAccountInput, "portfolioId"> }>({
    mutationFn: ({ id, body }) =>
      apiFetch<Account>(`/api/portfolio/accounts/${id}/close`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...body, portfolioId }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.portfolio.accountsForPortfolio(portfolioId) });
      void queryClient.invalidateQueries({ queryKey: qk.portfolio.summaryForPortfolio(portfolioId) });
      void queryClient.invalidateQueries({ queryKey: qk.portfolio.positionsForPortfolio(portfolioId) });
    },
  });
}

/** Delete an account by id. Invalidates accounts, positions, summary. */
export function useDeleteAccount() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) =>
      apiFetch<void>(`/api/portfolio/accounts/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.portfolio.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Mutations — Exchange Rates
// ---------------------------------------------------------------------------

/** Create an exchange rate. Invalidates positions (USD conversion), summary, exchangeRates. */
export function useCreateExchangeRate() {
  return useApiMutation<CreateExchangeRateInput, ExchangeRate>(
    "/api/portfolio/exchange-rates",
    "POST",
    {
      invalidateKeys: [qk.portfolio.all],
    },
  );
}

/** Refresh exchange rates (optionally as-of a date). Invalidates positions, summary, exchangeRates. */
export function useRefreshExchangeRates() {
  const queryClient = useQueryClient();
  return useMutation<{ refreshed: number }, Error, { date?: string }>({
    mutationFn: ({ date }) =>
      apiFetch<{ refreshed: number }>("/api/portfolio/exchange-rates/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(date ? { date } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.portfolio.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Mutations — Bulk import
// ---------------------------------------------------------------------------

/** Bulk import transactions. Invalidates positions, summary, transactions, cashflows, accounts (auto-create), symbols (auto-create). */
export function useBulkImportTransactions(portfolioId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    {
      created: number;
      updated: number;
      imported: number;
      total: number;
      errors: Array<{ row: number; error: string }>;
    },
    Error,
    Record<string, unknown>[]
  >({
    mutationFn: (rows) =>
      apiFetch<{
        created: number;
        updated: number;
        imported: number;
        total: number;
        errors: Array<{ row: number; error: string }>;
      }>("/api/portfolio/transactions/bulk", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ portfolioId, rows }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.portfolio.all });
      void queryClient.invalidateQueries({ queryKey: qk.symbols.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Queries — Compare
// ---------------------------------------------------------------------------

/** Read: compare portfolio summary between two dates (on-demand via `enabled`). */
function comparisonStatusTime(status: PortfolioComparisonSyncStatus): number {
  return Math.max(Date.parse(status.startedAt), status.completedAt ? Date.parse(status.completedAt) : 0,
    ...status.tasks.flatMap((task) => [task.startedAt ? Date.parse(task.startedAt) : 0, task.completedAt ? Date.parse(task.completedAt) : 0]));
}
function priorComparisonStatus(previous: PortfolioComparisonSyncStatus, incoming: PortfolioComparisonSyncStatus): boolean {
  return previous.attempt > incoming.attempt || previous.attempt === incoming.attempt && (
    !isPortfolioPriceSyncActive(previous.status) && isPortfolioPriceSyncActive(incoming.status) || comparisonStatusTime(previous) > comparisonStatusTime(incoming));
}
function terminalComparisonStatus(queryClient: QueryClient, status: PortfolioComparisonSyncStatus): PortfolioComparisonSyncStatus | null {
  const previous = queryClient.getQueryData<unknown>(qk.portfolio.comparisonCompletion(status.portfolioId, status.operationId));
  return isPortfolioComparisonSyncStatus(previous) && previous.from === status.from && previous.to === status.to ? previous : null;
}
function terminalVersion(status: PortfolioComparisonSyncStatus): string { return JSON.stringify([status.attempt, status.status, status.completedAt, status.sourceFingerprint]); }
/** Shared by SSE and the selected operation's sole Query poller; older attempts cannot replace a resumed job. */
export function acceptPortfolioComparisonStatus(queryClient: QueryClient, status: PortfolioComparisonSyncStatus): boolean {
  const key = qk.portfolio.comparisonOperation(status.portfolioId, status.from, status.to, status.operationId);
  const previous = queryClient.getQueryData<unknown>(key);
  const terminal = terminalComparisonStatus(queryClient, status);
  if (isPortfolioComparisonSyncStatus(previous) && priorComparisonStatus(previous, status) || terminal && priorComparisonStatus(terminal, status)) return false;
  // A poll started before this SSE/retry result must not overwrite the accepted attempt.
  void queryClient.cancelQueries({ queryKey: key, exact: true }, { silent: true, revert: true });
  queryClient.setQueryData(key, status); return true;
}
export function invalidatePortfolioComparisonAttempt(queryClient: QueryClient, status: PortfolioComparisonSyncStatus): void {
  if (isPortfolioPriceSyncActive(status.status)) return;
  const current = queryClient.getQueryData<unknown>(qk.portfolio.comparisonOperation(status.portfolioId, status.from, status.to, status.operationId));
  if (isPortfolioComparisonSyncStatus(current) && current.attempt > status.attempt) return;
  const terminal = terminalComparisonStatus(queryClient, status);
  if (terminal && (priorComparisonStatus(terminal, status) || terminalVersion(terminal) === terminalVersion(status))) return;
  queryClient.setQueryData(qk.portfolio.comparisonCompletion(status.portfolioId, status.operationId), status);
  void queryClient.invalidateQueries({ queryKey: qk.portfolio.compare(status.portfolioId, status.from, status.to), exact: true });
}
function matchingComparisonOperation(status: unknown, scope: { portfolioId: string; from: string; to: string; operationId?: string }): status is PortfolioComparisonSyncStatus {
  return isPortfolioComparisonSyncStatus(status) && status.portfolioId === scope.portfolioId && status.from === scope.from && status.to === scope.to
    && (scope.operationId === undefined || status.operationId === scope.operationId);
}
export function useRetryPortfolioComparison(portfolioId: string, from: string, to: string, operationId: string) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (scope: { portfolioId: string; from: string; to: string; operationId: string }) => {
      if (!scope.portfolioId || !scope.operationId) throw new Error("Нет выбранной операции подготовки сравнения");
      const status = await apiFetch<unknown>(`/api/portfolio/price-sync/${encodeURIComponent(scope.operationId)}/retry`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ portfolioId: scope.portfolioId }), timeoutMs: 30_000,
      });
      if (!matchingComparisonOperation(status, scope)) throw new Error("Ответ подготовки относится к другому портфелю или периоду");
      return status;
    },
    onSuccess: (status) => { if (acceptPortfolioComparisonStatus(queryClient, status)) invalidatePortfolioComparisonAttempt(queryClient, status); },
  });
  const scope = { portfolioId, from, to, operationId };
  return { ...mutation, mutate: () => mutation.mutate(scope), mutateAsync: () => mutation.mutateAsync(scope) };
}
export function usePortfolioCompare(
  portfolioId: string = "",
  from: string,
  to: string,
  enabled: boolean,
) {
  const queryClient = useQueryClient();
  const comparison = useApiQuery<PortfolioCompareResponse>(
    qk.portfolio.compare(portfolioId, from, to),
    `/api/portfolio/summary/compare?portfolioId=${encodeURIComponent(portfolioId)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    { enabled, timeoutMs: 30_000, retry: false, refetchOnReconnect: false },
  );
  const scope = { portfolioId, from, to };
  const reportedOperation = comparison.data?.preparation?.operation;
  const candidate = enabled && matchingComparisonOperation(reportedOperation, scope) ? reportedOperation : null;
  const operationId = candidate?.operationId ?? "";
  const operation = useApiQuery<PortfolioComparisonSyncStatus>(qk.portfolio.comparisonOperation(portfolioId, from, to, operationId),
    `/api/portfolio/price-sync/${encodeURIComponent(operationId)}?portfolioId=${encodeURIComponent(portfolioId)}`, {
      enabled: enabled && candidate !== null, ...(candidate ? { initialData: candidate } : {}), retry: false, timeoutMs: 30_000,
      // The comparison response already contains a validated initial status.
      // Only the single interval/SSE reconnect invalidation requests a fresher one.
      staleTime: Infinity, refetchOnMount: false, refetchOnReconnect: false,
      select: (status) => {
        if (!matchingComparisonOperation(status, { ...scope, operationId })) throw new Error("Получен статус другого сравнения");
        const terminal = terminalComparisonStatus(queryClient, status); return terminal && priorComparisonStatus(terminal, status) ? terminal : status;
      },
      refetchInterval: (query) => {
        if (!matchingComparisonOperation(query.state.data, { ...scope, operationId })) return false;
        const terminal = terminalComparisonStatus(queryClient, query.state.data);
        const status = terminal && priorComparisonStatus(terminal, query.state.data) ? terminal : query.state.data;
        return isPortfolioPriceSyncActive(status.status) ? PORTFOLIO_PRICE_STATUS_POLL_MS : false;
      },
    });
  useEffect(() => {
    if (!candidate || !acceptPortfolioComparisonStatus(queryClient, candidate)) return;
    if (!isPortfolioPriceSyncActive(candidate.status)) {
      queryClient.setQueryData(qk.portfolio.comparisonCompletion(candidate.portfolioId, candidate.operationId), candidate);
    }
  }, [candidate, queryClient]);
  useEffect(() => {
    if (enabled && matchingComparisonOperation(operation.data, { portfolioId, from, to, operationId })) invalidatePortfolioComparisonAttempt(queryClient, operation.data);
  }, [operation.data, enabled, portfolioId, from, to, operationId, queryClient]);
  const preparation = enabled && matchingComparisonOperation(operation.data, { ...scope, operationId }) && (!candidate || operation.data.attempt >= candidate.attempt) ? operation.data : candidate;
  const retryPreparation = useRetryPortfolioComparison(portfolioId, from, to, preparation?.operationId ?? "");
  return { ...comparison, preparation, preparationError: operation.error, isPreparing: preparation !== null && isPortfolioPriceSyncActive(preparation.status), retryPreparation };
}

// ---------------------------------------------------------------------------
// Queries — Yearly Performance
// ---------------------------------------------------------------------------

/** Read: yearly performance for a single year (optionally filtered by account). */
export function useYearlyPerformance(
  portfolioId: string = "",
  year: number,
  accountId?: string,
) {
  return useApiQuery<YearlyPerformance>(
    qk.portfolio.yearlyPerformance.single(portfolioId, year),
    buildUrl("/api/portfolio/yearly-performance", {
      portfolioId,
      year,
      accountId,
    }),
  );
}

/** Read: yearly performance for all years (optionally filtered by account). */
export function useYearlyPerformanceAll(
  portfolioId: string = "",
  accountId?: string,
  enabled: boolean = true,
) {
  return useApiQuery<{ items: YearlyPerformance[] }>(
    qk.portfolio.yearlyPerformance.all(portfolioId),
    buildUrl("/api/portfolio/yearly-performance-all", {
      portfolioId,
      accountId,
    }),
    { enabled: !!portfolioId && enabled },
  );
}
